import React, { useContext } from 'react';
import { Alert, Button } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import ContextBar, { ContextBarTabs } from '../common/ContextBar';
import RecordBadge from '../common/RecordBadge';
import { DataState, Skeleton, deriveDataState } from '../loading';
import ResourceState from '../loading/ResourceState';
import ShellSubNav from '../shell/ShellSubNav';
import { ShellActions, ShellSlotContext, useShellMenu, useShellSlot } from '../shell/ShellSlot';
import { CollectionContext } from '../../contexts/CollectionContext';
import { cardSubNav, collectionCardPath, collectionCardsPath, newCardPath } from '../../config/collectionShell';
import { getCardScope } from '../../utils/cardScopes';
import { getCollectionCardIds } from '../../utils/collections';
import { ancestorsOf, inheritedCardIdsOf } from '../../utils/collectionFamily';
import useWindowClass from '../../hooks/useWindowClass';
import { CARD_TAB } from './cardTabs';
import GlobalCardInCollection, { globalCardEditPath } from './GlobalCardInCollection';

/**
 * The card page's chrome (KPR-1282, KPE-039), kept out of CardDefinitionForm,
 * which is held to its file-size baseline.
 *
 * A collection card's page is `/collections/:id/cards/:cardId`, inside the
 * collection shell (docs/platform-standards.md §7, §13): the shell header is the
 * collection's, Cards is the current tab, and the page speaks to it through the
 * slot — its crumb tail (the card's name), its Cancel · Save changes, and its ⋯
 * entries for the header's one ⋯. The card's own tile and title lead the
 * content, and its sections are a left sub-nav, never a second tab row. The
 * platform route `/cards/:cardId` (a global card, an old link) still draws the
 * card's own context bar with its tabs.
 */

const normalizeId = (value) => (value && typeof value === 'object' ? String(value._id || value.$oid || '') : String(value || '')) || null;

/**
 * Whether the page is inside the collection shell, and that collection: the
 * slot is there (CollectionsLayout provides it) and the route names a
 * collection. `shellCollection` is the layout's loaded document, only when it
 * is the route's own.
 */
export function useCardShell() {
	const slot = useContext(ShellSlotContext);
	const { collectionId } = useParams();
	const layout = useContext(CollectionContext);
	const inShell = Boolean(slot) && Boolean(collectionId);
	const shellCollectionId = inShell ? String(collectionId) : null;
	const doc = layout?.collection;
	const shellCollection = inShell && doc && normalizeId(doc._id) === shellCollectionId ? doc : null;
	return { inShell, shellCollectionId, shellCollection, routeCollectionId: collectionId ? String(collectionId) : null };
}

const ownerOf = card => (getCardScope(card) === 'collection' ? normalizeId(card.collection_id) : null);
const withQuery = (path, params) => {
	const query = params.toString();
	return `${path}${query ? `?${query}` : ''}`;
};

/**
 * Whether the collection holds this card for its own use (KPR-1285): a
 * `cards[]` ref of its own, or a card it inherits from the collection above
 * (`hierarchy.inheritedCardIds`). A global card a sub-collection of it holds,
 * or one it only borrows through a link, is not held here.
 */
export function collectionHoldsCard(collection, cardId) {
	const id = String(cardId);
	return getCollectionCardIds(collection).has(id) || inheritedCardIdsOf(collection).has(id);
}

// A card this collection inherits from an ancestor this person cannot open
// (their access is on the sub-collection alone): its owner's page would answer
// "not found", so the card is drawn here, where they use it.
const inheritedFromClosedAncestor = (collection, ownerId, cardId) => inheritedCardIdsOf(collection).has(String(cardId))
	&& ancestorsOf(collection).some(ancestor => ancestor._id === ownerId && !ancestor.readable);

/**
 * In the shell, a card the route's collection does not own cannot be judged
 * until the collection itself has answered (KPR-1285): until then the page
 * waits, so a global card is never drawn and then sent away.
 */
export function cardAwaitsShell({ inShell, shellCollectionId, shellCollection, card }) {
	return Boolean(inShell && card && !shellCollection && ownerOf(card) !== shellCollectionId);
}

/**
 * Where a card goes instead of being drawn here, or null to stay. Judged only
 * on the loaded card the route names (never a previous card's document, never
 * a failed read, so a missing card cannot loop).
 *
 * In the shell of collection X:
 * - a collection card X owns stays; one owned elsewhere goes to its owner (a
 *   parent's card seen from a sub-collection lands in the parent), unless X
 *   inherits it from an ancestor this person cannot open, where it stays;
 * - a GLOBAL card X holds stays (KPR-1285): its home, with X's own settings
 *   for it. An admin who asks for an editing section goes to the platform
 *   editor, `/cards/:id?collectionId=X`, where a global card is edited. One X
 *   does not hold goes to `/cards/:id`, with no collection;
 * - an unassigned or legacy card goes to the platform page, saying it came
 *   from X as `/cards/:id` always has.
 *
 * On the platform page (`/cards/:id`, no shell collection) a collection card
 * goes to its owner (KPR-1283): an old link, a short link, or any door that
 * had only the id lands in the collection. A global card that names the
 * collection it came from (`?collectionId=X`) opens in X, except for an admin,
 * for whom that address is the global card's editor. Any other card stays.
 * Neither page sends a card back to the other, so nothing loops. A route that
 * names a collection with no shell above it (`routeCollectionId` without the
 * slot) is not the platform page and never redirects.
 *
 * The section (`?tab=`) and every other param go along.
 */
export function cardShellRedirect({ shellCollectionId, shellCollection = null, routeCollectionId = null, card, cardId, ready, search = '', admin = false }) {
	const newCardTarget = newCardRedirect({ shellCollectionId, routeCollectionId, cardId, search });
	if (newCardTarget) return newCardTarget;
	if (!ready || !card || normalizeId(card._id) !== String(cardId)) return null;
	const ownerId = ownerOf(card);
	const global = getCardScope(card) === 'global';
	const params = new URLSearchParams(search);
	const visited = params.get('collectionId');
	params.delete('collectionId');
	if (!shellCollectionId) {
		if (routeCollectionId) return null;
		if (ownerId) return withQuery(collectionCardPath(ownerId, cardId), params);
		return global && visited && !admin ? withQuery(collectionCardPath(visited, cardId), params) : null;
	}
	if (ownerId === shellCollectionId || !shellCollection) return null;
	if (ownerId) {
		return inheritedFromClosedAncestor(shellCollection, ownerId, cardId) ? null : withQuery(collectionCardPath(ownerId, cardId), params);
	}
	if (global && !collectionHoldsCard(shellCollection, cardId)) return withQuery(`/cards/${cardId}`, params);
	const tab = params.get('tab');
	if (global && !(admin && tab && tab !== CARD_TAB.overview)) return null;
	params.set('collectionId', shellCollectionId);
	return withQuery(`/cards/${cardId}`, params);
}

/**
 * The old door to a new collection card (KPR-1284): `/cards/new?collectionId=X`
 * on the platform page goes to `/collections/X/cards/new`, where the card is
 * made inside the collection that will own it. Every other param (the
 * `parentCardId` of Extend into a new card) goes along. A bare `/cards/new`
 * (an admin's global card) and the shell's own new card stay.
 */
export function newCardRedirect({ shellCollectionId = null, routeCollectionId = null, cardId, search = '' }) {
	if (shellCollectionId || routeCollectionId || !['new', '-1'].includes(String(cardId))) return null;
	const params = new URLSearchParams(search);
	const collectionId = params.get('collectionId');
	if (!collectionId) return null;
	params.delete('collectionId');
	const query = params.toString();
	return `${newCardPath(collectionId)}${query ? `?${query}` : ''}`;
}

// In the header's one ⋯ the card's entries sit beside the collection's: their
// keys are their own, and the two that could be read as the collection's say
// they are the card's. "Collection cards" is the Cards tab, already in view.
const SHELL_MENU_LABELS = { history: 'Card history…', export: 'Export card…' };
const shellMenuOf = (actions) => (Array.isArray(actions) ? actions : [])
	.filter(action => action && action.key !== 'collection')
	.map(action => ({ ...action, key: `card-${action.key}`, label: SHELL_MENU_LABELS[action.key] || action.label }));

// A section's label, with the 6px action dot while it holds unsaved edits.
const sectionLabel = (tab, dirty) => (dirty ? (
	<>
		{tab.label}
		<span className='context-tabs__dot' aria-hidden='true' />
		<span className='visually-hidden'> (unsaved changes)</span>
	</>
) : tab.label);

/**
 * The page around the card's content. `awaiting`: the card has not answered,
 * so nobody knows what this person may do: no actions, no ⋯, no sections, no
 * title — the children are the load state.
 */
export default function CardPageFrame({
	inShell = false,
	collectionId = null,
	cardId = null,
	awaiting = false,
	breadcrumb = [],
	record,
	title,
	crumbTail = null,
	actions = null,
	phoneActions = null,
	menuActions = null,
	tabs = [],
	activeTab,
	onTab,
	dirtyTabs = null,
	// A global card in the shell (KPR-1285): GlobalCardInCollection's props.
	// Its scope and this collection's settings for it lead the Overview, and an
	// admin's Edit the global card joins the header's one ⋯.
	globalCard = null,
	children,
}) {
	const { compact: phone } = useWindowClass();
	const navigate = useNavigate();
	useShellSlot({ crumbTail: awaiting ? null : crumbTail, off: !inShell });
	const editGlobal = globalCard?.admin ? [{
		key: 'edit-global',
		group: 'navigate',
		glyph: 'edit',
		label: 'Edit the global card',
		onClick: () => navigate(globalCardEditPath(normalizeId(globalCard.card?._id), normalizeId(globalCard.collection?._id))),
	}] : [];
	useShellMenu(inShell && !awaiting ? shellMenuOf([...editGlobal, ...(menuActions || [])]) : null);
	const sections = awaiting ? [] : tabs;
	const isDirty = key => Boolean(dirtyTabs && dirtyTabs.has(key));

	if (!inShell) {
		return (
			<>
				<ContextBar
					breadcrumb={breadcrumb}
					record={record}
					kind='card'
					title={title}
					actions={actions}
					menuActions={awaiting ? null : menuActions}
					menuLabel='Card actions'
				>
					{sections.length > 1 ? (
						<ContextBarTabs
							aria-label='Card sections'
							items={sections.map(tab => ({ key: tab.key, disabled: tab.disabled, label: sectionLabel(tab, isDirty(tab.key)) }))}
							activeKey={activeTab}
							onChange={onTab}
						/>
					) : null}
				</ContextBar>
				{children}
			</>
		);
	}

	const subNav = cardSubNav({ collectionId, cardId, sections })
		.map(item => ({ ...item, label: sectionLabel(item, isDirty(item.key)) }));
	return (
		<>
			{awaiting ? null : <ShellActions>{phone ? phoneActions : actions}</ShellActions>}
			{awaiting ? null : (
				// The card leads its own content: its tile and title (an h2, the shell
				// owns the h1), previewing edits live. No actions or ⋯ of its own:
				// those are the shell's.
				<ContextBar
					className='context-bar--in-shell card-def-form__head'
					record={record}
					kind='card'
					eyebrow='Card'
					title={title}
					titleAs='h2'
					nameTab={false}
					ownMenu
				/>
			)}
			<div className={`shell-subpage card-def-form__subpage${phone ? ' shell-subpage--phone' : ''}`}>
				{subNav.length > 1 ? (
					<ShellSubNav label='Card sections' activeKey={activeTab} onSelect={onTab} items={subNav} />
				) : null}
				<div className='shell-subpage__content'>
					{!awaiting && globalCard && activeTab === CARD_TAB.overview ? <GlobalCardInCollection {...globalCard} /> : null}
					{children}
				</div>
			</div>
		</>
	);
}

const SHELL_COPY = {
	missing: { state: 'not-found', title: 'Card not found', body: 'It may have been deleted, or it isn\'t shared with you.' },
	'no-access': { state: 'no-access', title: 'You don\'t have access to this card', body: 'Ask a manager of its collection to share it with you.' },
};

/**
 * An existing card that has not answered: a skeleton, the failure with Retry,
 * or the plain statement that it is not there (404) or not this person's
 * (403). In the shell it says so in the page's place, with the way back to the
 * collection's Cards tab; on the platform page, as it always has.
 */
export function CardLoadState({ inShell = false, loadState, collectionId = null, onRetry, onLeave }) {
	const copy = SHELL_COPY[loadState];
	if (copy && inShell) {
		return (
			<ResourceState
				state={copy.state}
				title={copy.title}
				body={copy.body}
				backTo={collectionCardsPath(collectionId)}
				backLabel='Go to cards'
				className='card-def-form__alert'
			/>
		);
	}
	if (copy) {
		return (
			<Alert
				type='error'
				showIcon
				className='card-def-form__alert'
				message='That card is not available.'
				description='It may have been deleted, or it is not yours to open.'
				action={<Button onClick={onLeave}>Go to cards</Button>}
			/>
		);
	}
	const error = loadState === 'error' ? 'Check your connection and try again.' : null;
	return (
		<DataState
			// 'ready' with no saved card cannot happen; if it ever did, it would wait, never draw nothing.
			state={deriveDataState({ loading: loadState !== 'error', error })}
			skeleton={<Skeleton variant='form-field' count={6} />}
			error={error}
			onRetry={onRetry}
			label='this card'
		/>
	);
}

/**
 * Scope's owner fact for a collection card: the collection it belongs to, as
 * its record badge, from the collection document in hand (the shell's, or the
 * one the platform page read), never the `include=usage` read.
 */
export function CardOwnerFact({ collection, readOnly = false }) {
	if (!collection) return null;
	const id = normalizeId(collection._id);
	const name = (collection.name || '').trim() || '(untitled collection)';
	return (
		<>
			<div className='card-def-form__usage' data-card-owner={id || undefined}>
				<span>Belongs to</span>
				<RecordBadge kind='collection' record={collection} title={name} size='xs' to={id ? `/collections/${id}` : undefined} />
			</div>
			<p>{readOnly ? `You don't manage ${name}, so this card is read-only here.` : `${name} and its sub-collections can use it.`}</p>
		</>
	);
}
